import {
  GameEvent,
  OvertakeGameEventPayload,
  PenaltyGameEventPayload,
  ScoreGameEventPayload,
  TurnEndEventPayload,
  ZoneTriggeredEventPayload,
  ZoneTriggeredLink,
} from './game';
import type { ZoneTier } from '../domain/zones';
import { eventPayloadSchemas } from './eventSchemas';

type AnyPayload = Record<string, unknown>;

function requireEventId(event: GameEvent): string {
  if (typeof event.id !== 'string' || event.id.trim().length === 0) {
    throw new Error(`Event ${event.type} is missing id`);
  }
  return event.id;
}

function requirePayloadEventId(payload: AnyPayload, event: GameEvent): string {
  if (typeof payload.eventId !== 'string' || payload.eventId.trim().length === 0) {
    throw new Error(`Event ${event.type} is missing payload.eventId`);
  }
  return payload.eventId;
}

function normalizeOvertakePayload(payload: AnyPayload, event: GameEvent): OvertakeGameEventPayload {
  const eventId = requirePayloadEventId(payload, event);
  const penalties = Array.isArray(payload?.penalties)
    ? payload.penalties
        .map((penalty) => {
          if (!penalty || typeof penalty !== 'object') return null;
          const candidate = penalty as { playerId?: unknown; delta?: unknown };
          const playerId = typeof candidate.playerId === 'string' ? candidate.playerId : '';
          const delta = Number(candidate.delta ?? 0);
          return { playerId, delta };
        })
        .filter((penalty): penalty is { playerId: string; delta: number } => (
          penalty !== null && Boolean(penalty.playerId) && Number.isFinite(penalty.delta)
        ))
    : [];
  const rawBonus = Number(payload?.bonus ?? 0);
  const stages = Array.isArray(payload?.stages)
    ? (payload.stages as unknown as OvertakeGameEventPayload['stages'])
    : undefined;
  return {
    overtakerId: String(payload?.overtakerId || ''),
    bonus: rawBonus,
    penalties,
    stages,
    eventId,
  };
}

function normalizePenaltyPayload(payload: AnyPayload, event: GameEvent): PenaltyGameEventPayload {
  const eventId = requirePayloadEventId(payload, event);
  const rawResulting = payload?.resultingScore;
  const parsedResulting = rawResulting == null ? undefined : Number(rawResulting);
  return {
    playerId: String(payload?.playerId || ''),
    delta: Number(payload?.delta ?? 0),
    reason: String(payload?.reason || ''),
    eventId,
    ...(Number.isFinite(parsedResulting) ? { resultingScore: parsedResulting as number } : {}),
  };
}

function normalizeScorePayload(payload: AnyPayload, event: GameEvent): ScoreGameEventPayload {
  const eventId = requirePayloadEventId(payload, event);
  const delta = Number(payload?.delta ?? 0);
  const rawResulting = payload?.resultingScore;
  const parsedResulting = rawResulting == null ? undefined : Number(rawResulting);
  return {
    playerId: String(payload?.playerId || ''),
    delta,
    basePoints: payload?.basePoints != null ? Number(payload.basePoints) : undefined,
    eventId,
    ...(Number.isFinite(parsedResulting) ? { resultingScore: parsedResulting as number } : {}),
  };
}

function normalizeTurnEndPayload(payload: AnyPayload, event: GameEvent): TurnEndEventPayload {
  const eventId = requirePayloadEventId(payload, event);
  const overshotWinScore = payload?.overshotWinScore === true;
  return {
    playerId: String(payload?.playerId || ''),
    finalDice: Array.isArray(payload?.finalDice) ? payload.finalDice.map(Number) : [],
    basePoints: Number(payload?.basePoints ?? 0),
    ...(overshotWinScore ? { overshotWinScore: true } : {}),
    eventId,
  };
}

function normalizePenaltyList(raw: unknown): { playerId: string; delta: number }[] {
  return Array.isArray(raw)
    ? raw
        .map((penalty) => {
          if (!penalty || typeof penalty !== 'object') return null;
          const candidate = penalty as { playerId?: unknown; delta?: unknown };
          const playerId = typeof candidate.playerId === 'string' ? candidate.playerId : '';
          const delta = Number(candidate.delta ?? 0);
          return { playerId, delta };
        })
        .filter((penalty): penalty is { playerId: string; delta: number } => (
          penalty !== null && Boolean(penalty.playerId) && Number.isFinite(penalty.delta)
        ))
    : [];
}

function normalizeZoneTier(raw: unknown): ZoneTier {
  const tier = Number(raw ?? 3);
  return tier === 1 || tier === 2 ? tier : 3;
}

function normalizeZoneLinks(raw: unknown): ZoneTriggeredLink[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  return raw
    .map((entry) => {
      if (!entry || typeof entry !== 'object') return null;
      const link = entry as Record<string, unknown>;
      const zoneId = String(link.zoneId || '');
      if (!zoneId) return null;
      return {
        zoneId,
        kind: link.kind === 'penalty' ? 'penalty' : 'bonus',
        tier: normalizeZoneTier(link.tier),
        start: Number(link.start ?? 0),
        end: Number(link.end ?? 0),
        zoneDelta: Number(link.zoneDelta ?? 0),
        overtakeBonus: Number(link.overtakeBonus ?? 0),
        penalties: normalizePenaltyList(link.penalties),
        triggerScore: Number(link.triggerScore ?? 0),
        resultingScore: Number(link.resultingScore ?? 0),
      } satisfies ZoneTriggeredLink;
    })
    .filter((link): link is ZoneTriggeredLink => link !== null);
}

function normalizeZoneLoop(raw: unknown): { startIndex: number; endIndex: number } | null {
  if (!raw || typeof raw !== 'object') return null;
  const loop = raw as { startIndex?: unknown; endIndex?: unknown };
  const startIndex = Number(loop.startIndex);
  const endIndex = Number(loop.endIndex);
  if (!Number.isFinite(startIndex) || !Number.isFinite(endIndex)) return null;
  return { startIndex, endIndex };
}

function normalizeZoneTriggeredPayload(payload: AnyPayload, event: GameEvent): ZoneTriggeredEventPayload {
  const eventId = requirePayloadEventId(payload, event);
  const penalties = normalizePenaltyList(payload?.penalties);
  const links = normalizeZoneLinks(payload?.links);
  return {
    playerId: String(payload?.playerId || ''),
    zoneId: String(payload?.zoneId || ''),
    kind: payload?.kind === 'penalty' ? 'penalty' : 'bonus',
    tier: normalizeZoneTier(payload?.tier),
    start: Number(payload?.start ?? 0),
    end: Number(payload?.end ?? 0),
    zoneDelta: Number(payload?.zoneDelta ?? 0),
    overtakeBonus: Number(payload?.overtakeBonus ?? 0),
    penalties,
    triggerScore: Number(payload?.triggerScore ?? 0),
    resultingScore: Number(payload?.resultingScore ?? 0),
    // Brak `links` zostaje brakiem – zdarzenia sprzed łańcuchów mają opisywać
    // pojedyncze trafienie polami płaskimi, a nie udawać jednoogniwowy łańcuch.
    ...(links ? { links, settledIndex: Number(payload?.settledIndex ?? links.length) } : {}),
    ...(links ? { loop: normalizeZoneLoop(payload?.loop) } : {}),
    eventId,
  };
}

export function normalizeEventForRpc<E extends GameEvent>(event: E): E {
  const eventId = requireEventId(event);
  const base: GameEvent = { ...event, id: eventId };
  switch (base.type) {
    case 'OVERTAKE':
      return { ...base, payload: normalizeOvertakePayload(base.payload as unknown as AnyPayload, base) } as E;
    case 'PENALTY':
      return { ...base, payload: normalizePenaltyPayload(base.payload as unknown as AnyPayload, base) } as E;
    case 'SCORE':
      return { ...base, payload: normalizeScorePayload(base.payload as unknown as AnyPayload, base) } as E;
    case 'TURN_END':
      return { ...base, payload: normalizeTurnEndPayload(base.payload as unknown as AnyPayload, base) } as E;
    case 'ZONE_TRIGGERED':
      return { ...base, payload: normalizeZoneTriggeredPayload(base.payload as unknown as AnyPayload, base) } as E;
    default:
      if (base.payload && typeof base.payload === 'object') {
        requirePayloadEventId(base.payload as unknown as AnyPayload, base);
      }
      break;
  }
  return base as E;
}

// Eksport kontraktu (np. do dokumentacji lub sanity checks)
export const eventContracts = eventPayloadSchemas;
